import type { UIRouter } from "@uirouter/core";
import type { HookResult, Transition } from "@uirouter/react";

import { SI_ROOT_ROUTE } from "app/pages/sales-intelligence/constants/routes";

import { WI_TO_SI_AD_INTEL_ROUTES } from "../constants/wiToSiAdIntelRoutes";

/**
 * Resolves a target Web Intelligence Ad Intelligence state name to its SI equivalent.
 * Returns `undefined` if no SI mirror is registered for that WI state.
 */
const resolveSiAdIntelRoute = (wiStateName: string | undefined | null): string | undefined => {
    if (!wiStateName) return undefined;
    return WI_TO_SI_AD_INTEL_ROUTES[wiStateName];
};

/**
 * `transitionService.onBefore` hook that keeps the user inside Sales Intelligence.
 *
 * Reused Web Intelligence components internally navigate to native WI state names
 * (hardcoded into `onClick`/CTAs/recents cards). When a transition originates from a SI
 * state and targets a WI Ad Intelligence state that has an SI mirror in
 * {@link WI_TO_SI_AD_INTEL_ROUTES}, it is rewritten to that SI state with the same params
 * and transition options — the user never leaves SI.
 *
 * Runs in the `onBefore` phase, which precedes `onStart` — where UI-Router executes the
 * built-in `redirectTo` hook (e.g. WI `paramsResetHandler`). Rewriting the target here
 * therefore guarantees the original params survive before any WI redirect pipeline can
 * strip them.
 *
 * Registered/deregistered with SI lifecycle via {@link registerWiToSiRedirect}; the hook
 * itself is solution-scoped by the leading `from` guard so it never affects other solutions.
 */
const redirectWiToSi = (transition: Transition): HookResult => {
    const fromName = transition.from()?.name ?? "";
    if (!fromName.startsWith(`${SI_ROOT_ROUTE}-`)) return undefined;
    const toName = transition.to().name;
    const siTarget = resolveSiAdIntelRoute(toName);
    if (!siTarget) return undefined;
    return transition.router.stateService.target(siTarget, transition.params("to"), transition.options());
};

// Higher priority runs earlier within the `onBefore` phase. Bumped above the default (0)
// so the rewrite wins over the sibling config-fetch `onBefore` hook in `routerOnBefore`,
// avoiding a wasted WI component-config fetch before the redirect supersedes the transition.
const WI_TO_SI_REDIRECT_PRIORITY = 100;

/**
 * Registers {@link redirectWiToSi} as a scoped `onBefore` hook on the given router and
 * returns the deregister function. Call from a SI root component's effect so the hook is
 * active only while SI is mounted — keeping this intercept out of the global `routerOnBefore`.
 */
export const registerWiToSiRedirect = (router: UIRouter): (() => void) => {
    const deregister = router.transitionService.onBefore(
        { to: (state) => !!resolveSiAdIntelRoute(state?.name) },
        redirectWiToSi,
        { priority: WI_TO_SI_REDIRECT_PRIORITY },
    );
    return () => deregister();
};
